import { useCallback, useEffect, useRef } from 'react';
import type { FilterBarHandle } from '@alltrails/denali/components/FilterBar';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import styles from './useFilterBarScroll.module.scss';

// Width of the edge fade shown when the filter row can scroll further in that direction.
const SCROLL_FADE_WIDTH = 24;
// How far the pointer must move before a press turns into a drag (vs. a chip click).
const DRAG_THRESHOLD_PX = 5;

/**
 * Owns the explore filter bar's horizontal-scroll behavior — this is intentionally kept out of the denali
 * FilterBar since it's specific to rendering the bar over the map. Returns a ref to place on a wrapper
 * around the FilterBar, a ref to place on the FilterBar itself, a class to merge into the FilterBar's
 * `filtersContainerClassName`, and open/close handlers to wire to the bar's dropdowns. It does three things:
 *
 * 1. Edge fade: the scrollable edge(s) fade out to hint there's more to scroll, via a `mask` on the scroll
 *    container. The dropdowns are portaled (see FilterBar's `usePortalForDropdowns`) so they render outside
 *    this masked subtree — the fade can therefore stay on permanently, even with a dropdown open.
 * 2. Close-on-scroll: closes the open dropdown (via the FilterBar's imperative handle) when the user scrolls
 *    the row, otherwise the popover tracks its anchor (floating-ui autoUpdate) and can overlap other UI once
 *    scrolled far enough.
 * 3. Drag-to-scroll: lets a mouse click-and-drag the overflowing row sideways (a11y for mice without a
 *    horizontal wheel). Mouse-only (touch/pen keep native scrolling) and disabled while a dropdown is open
 *    so it never hijacks a slider/checkbox drag; the click that ends a drag is swallowed so it can't also
 *    toggle a chip.
 *
 * All get disabled on mobile sized screens, as we show a modal instead of a popover for explore filters.
 */
export default function useFilterBarScroll() {
  const isMobile = useIsMobileSizedScreen();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<FilterBarHandle>(null);
  const isDropdownOpenRef = useRef(false);
  const isDraggingRef = useRef(false);

  // Drive the per-side fade width from the scroll position, re-measuring on scroll, resize (container width),
  // and content changes (pills added/removed/relabeled). On a user scroll, also close the open dropdown.
  useEffect(() => {
    if (isMobile) {
      return undefined;
    }
    const scrollContainer = wrapperRef.current?.querySelector<HTMLElement>(`.${styles.scrollFadeContainer}`);
    if (!scrollContainer) {
      return undefined;
    }

    const canScroll = () => scrollContainer.scrollWidth > scrollContainer.clientWidth;

    const updateFade = () => {
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.style.setProperty('--filters-fade-start', scrollContainer.scrollLeft > 1 ? `${SCROLL_FADE_WIDTH}px` : '0px');
      scrollContainer.style.setProperty('--filters-fade-end', scrollContainer.scrollLeft < maxScrollLeft - 1 ? `${SCROLL_FADE_WIDTH}px` : '0px');
      // Hint draggability with a grab cursor whenever there's overflow (left untouched mid-drag).
      if (!isDraggingRef.current) {
        scrollContainer.style.cursor = canScroll() ? 'grab' : '';
      }
    };

    const onScroll = () => {
      updateFade();
      if (isDropdownOpenRef.current) {
        filterBarRef.current?.closeActiveFilter();
      }
    };

    updateFade();
    scrollContainer.addEventListener('scroll', onScroll, { passive: true });

    // Content/size changes shouldn't close the dropdown — only a real scroll should — so observers only update the fade.
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateFade) : null;
    resizeObserver?.observe(scrollContainer);

    const mutationObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(updateFade) : null;
    mutationObserver?.observe(scrollContainer, { childList: true, subtree: true, characterData: true });

    // --- Drag-to-scroll (mouse only) ---
    let pointerDownX = 0;
    let scrollLeftAtPointerDown = 0;
    let isPointerDown = false;
    let isDragging = false;
    // Set after a drag so the trailing click can't toggle a chip; cleared once it fires or the next press starts.
    let swallowNextClick: ((clickEvent: MouseEvent) => void) | null = null;

    const clearSwallowClick = () => {
      if (swallowNextClick) {
        scrollContainer.removeEventListener('click', swallowNextClick, true);
        swallowNextClick = null;
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      // Left mouse button only; skip while a dropdown is open so we don't fight a slider/checkbox drag.
      if (e.button !== 0 || e.pointerType !== 'mouse' || isDropdownOpenRef.current || !canScroll()) {
        return;
      }
      clearSwallowClick();
      isPointerDown = true;
      isDragging = false;
      pointerDownX = e.clientX;
      scrollLeftAtPointerDown = scrollContainer.scrollLeft;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isPointerDown) {
        return;
      }
      const dx = e.clientX - pointerDownX;
      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) {
          return;
        }
        isDragging = true;
        isDraggingRef.current = true;
        scrollContainer.setPointerCapture(e.pointerId);
        scrollContainer.style.cursor = 'grabbing';
        scrollContainer.style.userSelect = 'none';
      }
      scrollContainer.scrollLeft = scrollLeftAtPointerDown - dx;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isPointerDown) {
        return;
      }
      isPointerDown = false;
      if (!isDragging) {
        return;
      }
      isDragging = false;
      isDraggingRef.current = false;
      if (scrollContainer.hasPointerCapture(e.pointerId)) {
        scrollContainer.releasePointerCapture(e.pointerId);
      }
      scrollContainer.style.userSelect = '';
      updateFade(); // restore the grab cursor
      swallowNextClick = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
        clearSwallowClick();
      };
      scrollContainer.addEventListener('click', swallowNextClick, true);
    };

    scrollContainer.addEventListener('pointerdown', onPointerDown);
    scrollContainer.addEventListener('pointermove', onPointerMove);
    scrollContainer.addEventListener('pointerup', onPointerUp);
    scrollContainer.addEventListener('pointercancel', onPointerUp);

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scrollContainer.removeEventListener('pointerdown', onPointerDown);
      scrollContainer.removeEventListener('pointermove', onPointerMove);
      scrollContainer.removeEventListener('pointerup', onPointerUp);
      scrollContainer.removeEventListener('pointercancel', onPointerUp);
      clearSwallowClick();
    };
  }, [isMobile]);

  // Tracked only for close-on-scroll and to keep drag-to-scroll from hijacking an open dropdown's controls.
  const onDropdownOpen = useCallback(() => {
    isDropdownOpenRef.current = true;
  }, []);
  const onDropdownClose = useCallback(() => {
    isDropdownOpenRef.current = false;
  }, []);

  return {
    wrapperRef,
    filterBarRef,
    filtersContainerClassName: isMobile ? undefined : styles.scrollFadeContainer,
    onDropdownOpen,
    onDropdownClose
  };
}
